import { isWeb, getPlatform } from '../src/utils/platformUtils'
import { LEARNING_STORE_NAMES } from '../src/db/constants'

// Exportar LEARNING_STORE_NAMES para uso en otros archivos de database
export { LEARNING_STORE_NAMES }

// Utilidad para parseo seguro de JSON o aceptar estructuras ya materializadas
const safeParseJSON = (val, def = []) => {
    if (val == null) return def
    if (Array.isArray(val)) return val
    if (typeof val === 'object') return val
    if (typeof val === 'string') {
        try {
            return JSON.parse(val)
        } catch {
            return def
        }
    }
    return def
}

let __dbModule = null
const buildLocalWrappedDb = () => {
    if (globalThis.__DOTS_SQLITE_WRAPPED_DB) {
        return globalThis.__DOTS_SQLITE_WRAPPED_DB
    }
    try {
        const { Platform } = require('react-native')
        if (Platform?.OS === 'web') throw new Error('SQLite not available (web)')
        const localDb = require('expo-sqlite').openDatabaseSync('myDB')
        if (getPlatform().OS === 'android') {
            try {
                localDb.execSync('PRAGMA journal_mode = WAL;')
            } catch {}
        }
        const wrapStatement = (stmt) => ({
            executeSync: (params = []) => {
                const res = stmt.executeSync(params)
                const rowsArray = Array.isArray(res?.rows) ? res.rows : Array.isArray(res?.rows?._array) ? res.rows._array : []
                return {
                    ...res,
                    getAllSync: () => rowsArray,
                    getFirstSync: () => (rowsArray && rowsArray.length ? rowsArray[0] : null),
                }
            },
            finalizeSync: () => stmt.finalizeSync(),
        })
        const wrapped = {
            execSync: (sql) => localDb.execSync(sql),
            prepareSync: (sql) => {
                const stmt = localDb.prepareSync(sql)
                const wrapped = wrapStatement(stmt)
                return {
                    executeSync: wrapped.executeSync,
                    finalizeSync: wrapped.finalizeSync,
                    getAllSync: (params = []) => wrapped.executeSync(params).getAllSync(),
                    getFirstSync: (params = []) => wrapped.executeSync(params).getFirstSync(),
                }
            },
        }
        globalThis.__DOTS_SQLITE_WRAPPED_DB = wrapped
        return wrapped
    } catch (e) {
        console.error('Fallback SQLite wrapper (learning) failed:', e)
        return null
    }
}
const getWrappedDB = () => {
    if (!__dbModule) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        __dbModule = require('../src/db/indexDB')
    }
    const fromIndex = __dbModule?.getNativeDB ? __dbModule.getNativeDB() : null
    return fromIndex || buildLocalWrappedDb()
}

const openDatabase = () => {
    if (isWeb()) {
        // Web: usar el adaptador universal de DB que expone transacciones estilo IndexedDB
        try {
            const { getDatabaseAdapter } = require('../src/db/DatabaseAdapter')
            return getDatabaseAdapter('myDB')
        } catch (e) {
            console.error('[learning.database] Web adapter not available:', e?.message)
            // Fallback mínimo: mantiene firma pero no soporta objectStore
            return {
                transaction: () => ({
                    objectStore: () => ({ get: () => ({ onsuccess: null, onerror: null }), getAll: () => ({ onsuccess: null, onerror: null }) }),
                }),
            }
        }
    }

    const dbSync = getWrappedDB()
    const facade = {
        execSync: (sql) => dbSync.execSync(sql),
        prepareSync: (sql) => dbSync.prepareSync(sql),
        transaction: (callback, errorCallback, successCallback) => {
            try {
                const tx = {
                    executeSql: (sql, params = [], success, error) => {
                        try {
                            const isSelect = /^\s*SELECT/i.test(sql)
                            if (isSelect) {
                                const stmt = dbSync.prepareSync(sql)
                                const rowsArray = stmt.getAllSync(params)
                                stmt.finalizeSync()
                                const rows = {
                                    length: rowsArray.length,
                                    item: (i) => rowsArray[i],
                                    _array: rowsArray,
                                }
                                const result = { rows, rowsAffected: 0, insertId: undefined }
                                success && success(tx, result)
                            } else {
                                const stmt = dbSync.prepareSync(sql)
                                stmt.executeSync(params)
                                stmt.finalizeSync()
                                const changes = dbSync.prepareSync('SELECT changes() as rowsAffected').getFirstSync()
                                const lastId = /\bINSERT\b/i.test(sql)
                                    ? dbSync.prepareSync('SELECT last_insert_rowid() as insertId').getFirstSync()
                                    : { insertId: undefined }
                                const result = {
                                    rows: { length: 0, item: () => undefined, _array: [] },
                                    rowsAffected: changes?.rowsAffected ?? 0,
                                    insertId: lastId?.insertId,
                                }
                                success && success(tx, result)
                            }
                        } catch (e) {
                            if (error) return error(tx, e)
                            throw e
                        }
                    },
                }
                callback && callback(tx)
                successCallback && successCallback()
            } catch (e) {
                errorCallback && errorCallback(e)
            }
        },
    }

    return facade
}

export const db = openDatabase()

class learningServices {
    constructor() {
        this.db = db
    }

    /**
     * @description funcion para trareme un curso con sus contenidos en base al id
     * @param {*} courseId
     * @returns
     */
    get_course_by_id = (courseId) => {
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                try {
                    const txCourse = db.transaction([LEARNING_STORE_NAMES.COURSES], 'readonly')
                    const storeCourse = txCourse.objectStore(LEARNING_STORE_NAMES.COURSES)
                    const reqCourse = storeCourse.get(courseId)

                    reqCourse.onsuccess = (e) => {
                        const course = e.target.result
                        if (!course) {
                            resolve(null)
                            return
                        }

                        const modulIds = safeParseJSON(course.moduls, [])
                        const evaluationIds = safeParseJSON(course.evaluations, [])

                        const txContents = db.transaction([LEARNING_STORE_NAMES.CONTENTS], 'readonly')
                        const storeContents = txContents.objectStore(LEARNING_STORE_NAMES.CONTENTS)
                        const reqAllContents = storeContents.getAll()

                        reqAllContents.onsuccess = (ce) => {
                            const allContents = ce.target.result || []
                            const resolvedModuls = modulIds
                                .map((m) => {
                                    const id = m && typeof m === 'object' ? m._id : m
                                    return allContents.find((c) => c && c._id === id) || (typeof m === 'object' ? m : null)
                                })
                                .filter(Boolean)

                            const txEvals = db.transaction([LEARNING_STORE_NAMES.EVALUATIONS], 'readonly')
                            const storeEvals = txEvals.objectStore(LEARNING_STORE_NAMES.EVALUATIONS)
                            const reqAllEvals = storeEvals.getAll()

                            reqAllEvals.onsuccess = (ee) => {
                                const allEvals = ee.target.result || []
                                const resolvedEvaluations = evaluationIds
                                    .map((ev) => {
                                        const id = ev && typeof ev === 'object' ? ev._id : ev
                                        const found = allEvals.find((x) => x && x._id === id) || (typeof ev === 'object' ? ev : null)
                                        return found ? transformEvaluation(found) : null
                                    })
                                    .filter(Boolean)

                                resolve({
                                    ...course,
                                    moduls: resolvedModuls,
                                    evaluations: resolvedEvaluations,
                                })
                            }
                            reqAllEvals.onerror = (err) => reject(err?.target?.error || err)
                        }
                        reqAllContents.onerror = (err) => reject(err?.target?.error || err)
                    }
                    reqCourse.onerror = (err) => reject(err?.target?.error || err)
                } catch (err) {
                    reject(err)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${LEARNING_STORE_NAMES.COURSES} WHERE _id = ?;`,
                            [courseId],
                            async (_, result) => {
                                const course = result.rows._array[0]
                                // console.log("🚀 ~ course ~ result:", course)
                                if (!course) {
                                    //console.log('Course not found in local apps')
                                    resolve(null)
                                    return
                                }

                                let modulIds = course.moduls
                                try {
                                    // Primer parseo: elimina las comillas extra
                                    if (typeof modulIds === 'string' && modulIds.startsWith('"')) {
                                        modulIds = JSON.parse(modulIds)
                                    }
                                    // Segundo parseo: convierte el string JSON en array
                                    if (typeof modulIds === 'string') {
                                        modulIds = JSON.parse(modulIds)
                                    }
                                } catch (e) {
                                    modulIds = []
                                }
                                if (!Array.isArray(modulIds)) modulIds = []

                                let modulesPromises = []
                                for (const modulID of modulIds) {
                                    let modul = await this.get_content_by_id(modulID)
                                    if (!modul) continue
                                    modulesPromises.push(modul)
                                }

                                let evaluationsIds = course.evaluations
                                try {
                                    // Primer parseo: elimina las comillas extra
                                    if (typeof evaluationsIds === 'string' && evaluationsIds.startsWith('"')) {
                                        evaluationsIds = JSON.parse(evaluationsIds)
                                    }
                                    // Segundo parseo: convierte el string JSON en array
                                    if (typeof evaluationsIds === 'string') {
                                        evaluationsIds = JSON.parse(evaluationsIds)
                                    }
                                } catch (e) {
                                    evaluationsIds = []
                                }
                                if (!Array.isArray(evaluationsIds)) evaluationsIds = []
                                let evaluationsPromises = []
                                for (const evaluationID of evaluationsIds) {
                                    let ev = await this.get_evaluation_by_id(evaluationID)
                                    if (!ev) continue
                                    evaluationsPromises.push(ev)
                                }
                                console.log('INFO FINAl ===>', modulesPromises, '===>', evaluationsPromises)
                                Promise.all([Promise.all(modulesPromises), Promise.all(evaluationsPromises)])
                                    .then((response) => {
                                        const courseWithModulesAndEvaluations = {
                                            ...course,
                                            moduls: response[0],
                                            evaluations: response[1],
                                        }
                                        resolve(courseWithModulesAndEvaluations)
                                    })
                                    .catch((error) => {
                                        console.error('Error fetching modules:', error)
                                        reject(error)
                                    })
                            },
                            (_, error) => {
                                console.error('118 -- Error en la transacción course:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción course - sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }

    /**
     * @description funcion para traerme un contenido en base al id
     * @param {*} contentId
     * @returns
     */
    get_content_by_id = (contentId) => {
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([LEARNING_STORE_NAMES.CONTENTS], 'readonly')
                const objectStore = transaction.objectStore(LEARNING_STORE_NAMES.CONTENTS)

                const request = objectStore.get(contentId)

                request.onsuccess = (event) => {
                    const content = event.target.result
                    if (!content) {
                        resolve(null)
                        return
                    }
                    const modified_content = {
                        ...content,
                        status: typeof content.status === 'string' ? parseInt(content.status) : content.status,
                        evaluations: safeParseJSON(content.evaluations, []),
                        media_slides: safeParseJSON(content.media_slides, []),
                    }
                    resolve(modified_content)
                }
                request.onerror = (event) => reject(event.target.error)
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${LEARNING_STORE_NAMES.CONTENTS} WHERE _id = ?`,
                            [contentId],
                            (_, result) => {
                                console.log('Llego acá antes de romper==>', contentId)
                                if (result.rows.length > 0) {
                                    const content = result.rows.item(0)
                                    const modified_content = {
                                        ...content,
                                        status: typeof content.status === 'string' ? parseInt(content.status) : content.status,
                                        evaluations: safeParseJSON(content.evaluations, []),
                                        media_slides: safeParseJSON(content.media_slides, []),
                                    }
                                    resolve(modified_content)
                                } else {
                                    resolve(null) // El contenido no fue encontrado
                                }
                            },
                            (_, error) => {
                                console.error('Error al obtener el contenido por ID:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Transaction get_content_by_id error:', error)
                        reject(error)
                    },
                )
            }
        })
    }

    get_evaluation_by_id = (evaluationId) => {
        return new Promise((resolve, reject) => {
            if (isWeb()) {
                const transaction = db.transaction([LEARNING_STORE_NAMES.EVALUATIONS], 'readonly')
                const objectStore = transaction.objectStore(LEARNING_STORE_NAMES.EVALUATIONS)
                const request = objectStore.get(evaluationId)

                request.onsuccess = (event) => {
                    const evaluation = event.target.result
                    // Realiza las transformaciones necesarias
                    const modifiedEvaluation = transformEvaluation(evaluation)
                    resolve(modifiedEvaluation)
                }

                request.onerror = (event) => {
                    reject(event.target.error)
                }
            } else {
                db.transaction(
                    (tx) => {
                        tx.executeSql(
                            `SELECT * FROM ${LEARNING_STORE_NAMES.EVALUATIONS} WHERE _id = ?;`,
                            [evaluationId],
                            (_, result) => {
                                if (result.rows.length > 0) {
                                    const evaluation = result.rows.item(0)
                                    const modifiedEvaluation = transformEvaluation(evaluation)
                                    resolve(modifiedEvaluation)
                                } else {
                                    console.log('la evaluacion no fue encontrada ...')
                                    resolve(null) // La evaluación no fue encontrada
                                }
                            },
                            (_, error) => {
                                console.error('Error en la transacción evaluation by ID:', error)
                                reject(error)
                            },
                        )
                    },
                    (error) => {
                        console.error('Error en la transacción evaluation by ID -sqlite:', error)
                        reject(error)
                    },
                    null,
                )
            }
        })
    }
}

export const LearningServices = new learningServices(db)

//* funciones
const transformEvaluation = (evaluation) => {
    if (!evaluation) return null
    return {
        ...evaluation,
        type_assessment: safeParseJSON(evaluation.type_assessment, {}),
        questions: safeParseJSON(evaluation.questions, []),
        survey: safeParseJSON(evaluation.survey, {}),
    }
}
